import React, { createContext, useContext, useState, useEffect } from 'react';

const AuditContext = createContext();

export const useAudit = () => {
  const context = useContext(AuditContext);
  if (!context) {
    throw new Error('useAudit must be used within AuditProvider');
  }
  return context;
};

const initialState = {
  company: '',
  contact: {
    name: '',
    firstName: '',
    lastName: '',
    email: '',
    website: '',
    industry: '',
    employeeCount: '',
    role: ''
  },
  sections: {
    operations: {
      ratings: [0, 0, 0, 0, 0],
      notes: '',
      total: 0
    },
    customer: {
      ratings: [0, 0, 0, 0, 0],
      notes: '',
      total: 0
    },
    marketing: {
      ratings: [0, 0, 0, 0, 0],
      notes: '',
      total: 0
    },
    team: {
      ratings: [0, 0, 0, 0, 0],
      notes: '',
      total: 0
    }
  },
  tech_stack: {
    operations: {
      tools: [],
      other_tool: '',
      integration_rating: 0
    },
    customer: {
      tools: [],
      other_tool: '',
      integration_rating: 0
    },
    salesMarketing: {
      tools: [],
      other_tool: '',
      integration_rating: 0
    },
    team: {
      tools: [],
      other_tool: '',
      integration_rating: 0
    }
  },
  roi_items: [],
  matrix_items: [],
  calculator_result: '',
  top_frictions: ['', '', ''],
  readiness_inputs: {
    leadership_understands_ai: false,
    trustworthy_accessible_data: false,
    open_to_testing: false,
    budget_or_resources: false
  },
  risk_inputs: {
    approval_owner: '',
    sensitive_data: false,
    customer_messages: false,
    money_or_commitments: false
  }
};

export const AuditProvider = ({ children }) => {
  const [auditData, setAuditData] = useState(() => {
    const saved = localStorage.getItem('trilogyOneAudit');
    return saved ? JSON.parse(saved) : initialState;
  });

  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    localStorage.setItem('trilogyOneAudit', JSON.stringify(auditData));
  }, [auditData]);

  const updateContact = (field, value) => {
    setAuditData(prev => ({
      ...prev,
      contact: { ...prev.contact, [field]: value }
    }));
  };

  const updateCompany = (value) => {
    setAuditData(prev => ({ ...prev, company: value }));
  };

  const updateSectionRating = (section, questionIndex, rating) => {
    setAuditData(prev => {
      const newRatings = [...prev.sections[section].ratings];
      newRatings[questionIndex] = rating;
      const total = newRatings.reduce((sum, r) => sum + r, 0);
      return {
        ...prev,
        sections: {
          ...prev.sections,
          [section]: {
            ...prev.sections[section],
            ratings: newRatings,
            total
          }
        }
      };
    });
  };

  const updateSectionNotes = (section, notes) => {
    setAuditData(prev => ({
      ...prev,
      sections: {
        ...prev.sections,
        [section]: {
          ...prev.sections[section],
          notes
        }
      }
    }));
  };

  const addRoiItem = (item) => {
    setAuditData(prev => ({
      ...prev,
      roi_items: [...prev.roi_items, item]
    }));
  };

  const updateRoiItem = (index, item) => {
    setAuditData(prev => {
      const newItems = [...prev.roi_items];
      newItems[index] = item;
      return { ...prev, roi_items: newItems };
    });
  };

  const deleteRoiItem = (index) => {
    setAuditData(prev => ({
      ...prev,
      roi_items: prev.roi_items.filter((_, i) => i !== index)
    }));
  };

  const addMatrixItem = (item) => {
    setAuditData(prev => ({
      ...prev,
      matrix_items: [...prev.matrix_items, item]
    }));
  };

  const updateMatrixItem = (index, item) => {
    setAuditData(prev => {
      const newItems = [...prev.matrix_items];
      newItems[index] = item;
      return { ...prev, matrix_items: newItems };
    });
  };
  
  const updateMatrixItemRating = (roiIndex, field, value) => {
    setAuditData(prev => {
      const existingIndex = prev.matrix_items.findIndex(m => m.roi_index === roiIndex);
      let newItems = [...prev.matrix_items];
      
      if (existingIndex >= 0) {
        // Update existing
        newItems[existingIndex] = { ...newItems[existingIndex], [field]: value };
      } else {
        // Create new
        newItems.push({ roi_index: roiIndex, impact: 0, effort: 0, [field]: value });
      }
      
      return { ...prev, matrix_items: newItems };
    });
  };

  const deleteMatrixItem = (index) => {
    setAuditData(prev => ({
      ...prev,
      matrix_items: prev.matrix_items.filter((_, i) => i !== index)
    }));
  };

  const updateTopFrictions = (index, value) => {
    setAuditData(prev => {
      const newFrictions = [...prev.top_frictions];
      newFrictions[index] = value;
      return { ...prev, top_frictions: newFrictions };
    });
  };

  const updateCalculatorResult = (value) => {
    setAuditData(prev => ({ ...prev, calculator_result: value }));
  };

  const updateReadinessInput = (field, value) => {
    setAuditData(prev => ({
      ...prev,
      readiness_inputs: {
        ...initialState.readiness_inputs,
        ...(prev.readiness_inputs || {}),
        [field]: value
      }
    }));
  };

  const updateRiskInput = (field, value) => {
    setAuditData(prev => ({
      ...prev,
      risk_inputs: {
        ...initialState.risk_inputs,
        ...(prev.risk_inputs || {}),
        [field]: value
      }
    }));
  };

  // Tech Stack functions
  const updateTechStackTools = (category, tools) => {
    setAuditData(prev => ({
      ...prev,
      tech_stack: {
        ...prev.tech_stack,
        [category]: {
          ...prev.tech_stack[category],
          tools
        }
      }
    }));
  };

  const updateTechStackOtherTool = (category, otherTool) => {
    setAuditData(prev => ({
      ...prev,
      tech_stack: {
        ...prev.tech_stack,
        [category]: {
          ...prev.tech_stack[category],
          other_tool: otherTool
        }
      }
    }));
  };

  const updateTechStackRating = (category, rating) => {
    setAuditData(prev => ({
      ...prev,
      tech_stack: {
        ...prev.tech_stack,
        [category]: {
          ...prev.tech_stack[category],
          integration_rating: rating
        }
      }
    }));
  };


  const getOverallScore = () => {
    return Object.values(auditData.sections).reduce((sum, section) => sum + section.total, 0);
  };

  const getRangeLabel = (score) => {
    if (score <= 40) return 'Efficient';
    if (score <= 80) return 'Pilot AI';
    return 'High ROI Potential';
  };

  const getTotalRoiAnnual = () => {
    return auditData.roi_items.reduce((sum, item) => sum + (item.annual || 0), 0);
  };

  const resetAudit = () => {
    setAuditData(initialState);
    setCurrentPage(1);
    localStorage.removeItem('trilogyOneAudit');
  };

  const value = {
    auditData,
    setAuditData,
    currentPage,
    setCurrentPage,
    updateContact,
    updateCompany,
    updateSectionRating,
    updateSectionNotes,
    addRoiItem,
    updateRoiItem,
    deleteRoiItem,
    addMatrixItem,
    updateMatrixItem,
    updateMatrixItemRating,
    deleteMatrixItem,
    updateTopFrictions,
    updateCalculatorResult,
    updateReadinessInput,
    updateRiskInput,
    updateTechStackTools,
    updateTechStackOtherTool,
    updateTechStackRating,
    getOverallScore,
    getRangeLabel,
    getTotalRoiAnnual,
    resetAudit
  };

  return <AuditContext.Provider value={value}>{children}</AuditContext.Provider>;
};